import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * MarketNest buttons: flat, 4px radius, no shadow. `primary`, `outline`
 * (the system's Secondary), `ghost` and `destructive` come straight from the
 * spec; `subtle` and `danger` are the two shapes this app already needed,
 * rebuilt out of the same tokens.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-control font-semibold transition-colors ' +
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-ink hover:bg-brand-hover active:bg-brand-active',
        outline:
          'border-[1.5px] border-brand bg-transparent text-brand hover:bg-brand-tint ' +
          'active:border-brand-active active:text-brand-active',
        subtle:
          'border border-border-strong bg-surface-muted text-ink hover:bg-brand-tint ' +
          'hover:border-border-loud',
        ghost: 'bg-transparent text-ink-muted hover:bg-brand-tint hover:text-ink',
        destructive: 'bg-danger text-white hover:bg-danger-hover',
        danger: 'bg-transparent text-danger hover:bg-danger-tint',
      },
      size: {
        // Touch targets first: this runs on a phone inside the LINE app.
        sm: 'h-[34px] px-4 text-body-sm',
        md: 'h-[42px] px-6 text-[15px]',
        lg: 'h-[50px] w-full px-8 text-body',
        icon: 'size-[42px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ComponentProps<'button'> & VariantProps<typeof button>;

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button className={cn(button({ variant, size }), className)} {...props} />;
}
